import { endedOnLabel, leagueEndAt, leagueStartAt, leagueTypeKey } from "./leagueFormat";
import { isEntryClosed } from "./inviteCode";

const HOUR = 60 * 60 * 1000;
const DAY = 24 * HOUR;

// A countdown reads in ONE unit, never two, and never as a day of the week:
//
//   24h or more   3d
//   under 24h     4h
//   under 1h      59m … 1m
//
// One unit because the second one is noise at every scale a card cares about —
// "3d 8h" and "4h 0m" both made the reader do arithmetic to learn "3 days" and
// "4 hours". The floor is 1m: below that the league is starting (or ending) and
// getLeagueStatus moves it to the next state anyway.
const formatCountdown = (ms) => {
  if (ms <= 0) return "0m";
  const minutes = Math.floor(ms / 60000);
  if (minutes >= 60 * 24) return `${Math.floor(minutes / (60 * 24))}d`;
  if (minutes >= 60) return `${Math.floor(minutes / 60)}h`;
  return `${Math.max(1, minutes)}m`;
};

export const getLeagueStatus = (game, now = new Date()) => {
  if (!game) return null;
  // leagueEndAt, not `new Date(game.end_date)`. end_date is a DATE stored at
  // midnight of the final day, so comparing against it directly declared every
  // league "Ended" for the whole of its last day — and a one-day league from the
  // second it opened. See the note on leagueEndAt in leagueFormat.js; the client
  // reported it as "I tried starting a league on the 13th … it shows as ended
  // right away."
  const start = leagueStartAt(game);
  const end = leagueEndAt(game);

  if (game.status === "cancelled") return null;

  if (game.status === "completed" || (end && end < now)) {
    return { label: "Ended", tone: "ended", countdownLabel: "Ended", countdownValue: "" };
  }

  // A league still waiting on admin approval says so, and says nothing else: its
  // dates are provisional until it is approved, so a countdown against them
  // would be a promise nobody has made yet. Checked after "completed" so an
  // already-finished league is never re-labelled as pending.
  if (game.status === "pending") {
    return {
      label: "Pending approval",
      tone: "pending",
      countdownLabel: "",
      countdownValue: "",
    };
  }

  if (start && now < start) {
    const msUntilStart = start - now;
    const soon = msUntilStart <= DAY;
    return {
      label: soon ? "Starting Soon" : "Upcoming",
      tone: soon ? "soon" : "upcoming",
      countdownLabel: "Starts in",
      countdownValue: formatCountdown(msUntilStart),
    };
  }

  if (end) {
    return {
      label: "Live",
      tone: "live",
      countdownLabel: "Ends in",
      countdownValue: formatCountdown(end - now),
    };
  }

  return { label: "Live", tone: "live", countdownLabel: "Ends in", countdownValue: "—" };
};

// Public leagues earn QUSHi Coin rewards in three tiers based on member count:
//   5-50    → Tier 1 (1 stack)
//   51-100  → Tier 2 (2 stacks)
//   101+    → Tier 3 (3 stacks)
// Leagues below the 5-member threshold show a ghost stack to signal "rewards
// unlock as members join" without overpromising. Returns null when members
// data is genuinely unknown (still loading) so callers can skip rendering
// the chip instead of flashing a misleading "Locked" state.
export const getRewardTier = (membersCount) => {
  if (membersCount === null || membersCount === undefined) return null;
  const raw = Number(membersCount);
  const n = Number.isFinite(raw) && raw >= 0 ? Math.floor(raw) : 0;
  if (n >= 101) return { tier: 3, stacks: 3, label: "Tier 3 Reward" };
  if (n >= 51) return { tier: 2, stacks: 2, label: "Tier 2 Reward" };
  if (n >= 5) return { tier: 1, stacks: 1, label: "Tier 1 Reward" };
  return { tier: 0, stacks: 0, label: "Rewards unlock at 5 members" };
};

// Default sort for public leagues: "Starting Soon" first, then chronological
// by start_date ascending. Cancelled and Ended leagues are pushed to the
// bottom (in their own stable order) so the user sees actionable leagues
// first. The list is sorted in place safely (copy then sort).
const SORT_BUCKET = (game, now) => {
  if (game.status === "cancelled") return 3;
  if (game.status === "completed") return 2;
  // Same end instant the status line uses, so a league cannot be sorted into the
  // "finished" bucket while its own card still says "Ends in 4h".
  const end = leagueEndAt(game);
  if (end && end < now) return 2;
  return 0; // live + upcoming together, ordered chronologically below
};

export const sortByStartDateAsc = (games) => {
  if (!Array.isArray(games)) return [];
  const now = new Date();
  return [...games].sort((a, b) => {
    const ba = SORT_BUCKET(a, now);
    const bb = SORT_BUCKET(b, now);
    if (ba !== bb) return ba - bb;
    const sa = a?.start_date ? new Date(a.start_date).getTime() : Number.POSITIVE_INFINITY;
    const sb = b?.start_date ? new Date(b.start_date).getTime() : Number.POSITIVE_INFINITY;
    const saValid = Number.isFinite(sa);
    const sbValid = Number.isFinite(sb);
    if (!saValid && !sbValid) {
      // both broken — fall through to secondary key
    } else if (!saValid) {
      return 1;
    } else if (!sbValid) {
      return -1;
    } else if (sa !== sb) {
      return sa - sb;
    }
    // Secondary key keeps order deterministic for identical start_dates.
    const ida = Number(a?.id) || 0;
    const idb = Number(b?.id) || 0;
    return ida - idb;
  });
};

// A public league is created with status "pending" and waits on admin approval
// before it is listed, joinable or shareable (see GameController::createGame —
// cash-prize and private leagues go straight to "active").
//
// One helper, because four screens have to agree on the answer: the league's own
// header, the pre-join card's Join button, its Share button, and the join request
// itself. Reading `game.status` in each place is how they drift apart.
export const isPendingApproval = (game) => game?.status === "pending";

export const stripHtml = (text) => (text ? text.replace(/<[^>]+>/g, "") : "");

// Client Rev 2, private league landing (mobile AND desktop): "Fix description
// rendering so organizer entered line breaks and spacing are preserved."
//
// stripHtml above deletes every tag and puts NOTHING in its place, so the
// organiser's three paragraphs
//     <p>League Rules:</p><p>Be nice</p><p>Dont be a dick</p>
// came out as one run-on string — "League Rules:Be niceDont be a dick" — which is
// exactly what the client screenshotted. The paragraph breaks were not lost in
// the editor; they were lost here, at render time.
//
// This turns the block-level tags back into the newlines they stand for, decodes
// the entities a rich-text editor emits, and only then strips what is left. Three
// or more blank lines collapse to one gap so a stray run of empty paragraphs
// cannot push the buttons off the screen.
//
// stripHtml itself is untouched — it is used on tiles, headers and previews all
// over League Play, and this is a change to ONE screen's description.
export const descriptionToText = (html) => {
  if (!html) return "";
  return String(html)
    // <br> and every closing block tag are line breaks.
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/(p|div|li|h[1-6]|blockquote)\s*>/gi, "\n")
    // A list item reads as its own line even without the closing tag.
    .replace(/<li[^>]*>/gi, "")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/\r\n?/g, "\n")
    // Trailing spaces before a break would show as ragged indentation.
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
};

export const shortDescription = (text, limit = 12) => {
  const stripped = stripHtml(text).trim();
  if (!stripped) return "";
  const words = stripped.split(/\s+/);
  return words.length > limit ? `${words.slice(0, limit).join(" ")}…` : stripped;
};

// The status that sits top-right of a preview card, inline with the organiser's
// handle. Colours are the client's: dark teal for a league about to start, green
// for one that is running.
//
// Pending approval is a pill rather than plain type, and a deliberately quiet
// one — light neutral grey behind dark teal text. It is an administrative state,
// not a thing the reader is waiting on, so it must not compete with "Live".
export const previewStatus = (status, game) => {
  if (!status) return null;
  if (status.tone === "pending") {
    return { text: "Pending approval", cls: "bg-[#F3F4F6] text-[#004C50]", pill: true };
  }
  if (status.tone === "live") {
    return {
      text: status.countdownValue ? `Ends in ${status.countdownValue}` : "Live",
      cls: "text-[#35B23C]",
    };
  }
  // A finished league states WHEN it ended — "Ended" on its own leaves the
  // reader wondering whether it was yesterday or last year.
  if (status.tone === "ended") {
    return { text: endedOnLabel(game) || "Ended", cls: "text-tertiary" };
  }
  return {
    text: status.countdownValue ? `Starts in ${status.countdownValue}` : "Starting soon",
    cls: "text-[#006565]",
  };
};

// ── what the landing page's one button offers ────────────────────────────
// Client: "In my testing I click on an entry closed Live Now Public League
// chevron, and it went to the landing page with the muted Entry Closed button.
// This should be going to Leaderboard preview."
//
// The listing row already routes a live public league to the board
// (PublicLeagueRow.rowDestination). This is the other half: the landing page
// itself, which is still reachable by a share link, a bookmark or the browser's
// back button, and which answered a live league with a greyed-out "Entry closed"
// — a button that states a fact and offers nothing.
//
// It is the client's own model for the whole of League Play: "Starting soon ->
// opens the league/game landing page (join screen with the entry CTA). Live now
// -> opens the leaderboard preview page... Completed -> 'View Results', opens
// that same leaderboard preview page. So once a competition goes live, the
// landing page is retired... There is no live-state landing page with a CTA."
// The button now says the same thing the row does.
//
// ONE definition, read by the card's desktop CTA and by the phone's docked
// JoinBlock. Those two carried a copy each of the same four-branch ternary,
// which is how "Entry closed" ended up needing fixing in two places.
//
// PRIVATE LEAGUES ARE DELIBERATELY EXCLUDED. /leaderboards/<slug> has no
// membership gate — it loads whatever league the slug names — and a private
// league's slug is explicitly not a credential ("Knowing it grants no access,
// which is the whole point: the league name is not the password", inviteCode.js).
// Pointing a private league's non-member at that board would publish a private
// standings table to anyone holding the URL. The client's request named public
// leagues, so private keeps the CTA it has and is flagged to them separately.
//
// `blocked` is the one case with no destination left: a PRIVATE league whose
// entry has shut. It keeps the label and the greyed-out button it has today,
// because the alternative for it is the leaderboard and that is the door this
// function will not open. Public and cash games never reach it.
export const leagueCtaState = (game) => {
  const tone = getLeagueStatus(game)?.tone;
  // A member is never previewing — they are in. Same rule, and same priority,
  // as the listing row's.
  if (game?.isMember) return { label: "View League", action: "league" };

  if (leagueTypeKey(game) !== "private") {
    if (tone === "ended") return { label: "View Results", action: "leaderboard" };
    // Client: "When a league is still open, it should take the user to the
    // league landing page with the join now CTA." A live league that allows
    // late joins falls through to Join League below; only a closed one offers
    // the board.
    if (tone === "live" && !game?.join_after_start) {
      return { label: "View Leaderboard", action: "leaderboard" };
    }
  } else if (isEntryClosed(game)) {
    // Client, when `join_after_start` was still decorative: "Once a game starts,
    // no one should be able to enter the game other than members." The button
    // stops offering a join it cannot deliver and says so; the server refuses it
    // either way. UNCHANGED for private leagues — this is the state the client
    // signed off, and it is not the one they asked us to replace.
    return { label: "Entry closed", action: "join", blocked: true };
  }

  // Everything before the start, and the joinable states of a private league. A
  // league still awaiting approval lands here too and is held by the caller's
  // `pending` guard, exactly as before.
  return { label: "Join League", action: "join" };
};
